import ora, { Ora } from 'ora';
import pc from 'picocolors';

export interface ProgressOptions {
  text: string;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'cyan' | 'magenta';
  prefixText?: string;
}

export class ProgressManager {
  private spinner: Ora | null = null;
  private verbose: boolean;

  constructor(verbose = false) {
    this.verbose = verbose;
  }

  start(options: ProgressOptions): void {
    if (this.verbose) {
      console.log(pc.dim(`[DEBUG] ${options.text}`));
      return;
    }

    this.spinner = ora({
      text: options.text,
      color: options.color || 'cyan',
      prefixText: options.prefixText
    }).start();
  }

  update(text: string): void {
    if (this.verbose) {
      console.log(pc.dim(`[DEBUG] ${text}`));
      return;
    }

    if (this.spinner) {
      this.spinner.text = text;
    }
  }

  succeed(text?: string): void {
    if (this.verbose) {
      console.log(pc.green(`✓ ${text || 'Success'}`));
      return;
    }

    if (this.spinner) {
      this.spinner.succeed(text);
      this.spinner = null;
    }
  }

  fail(text?: string): void {
    if (this.verbose) {
      console.log(pc.red(`✗ ${text || 'Failed'}`));
      return;
    }

    if (this.spinner) {
      this.spinner.fail(text);
      this.spinner = null;
    }
  }

  warn(text?: string): void {
    if (this.verbose) {
      console.log(pc.yellow(`⚠ ${text || 'Warning'}`));
      return;
    }

    if (this.spinner) {
      this.spinner.warn(text);
      this.spinner = null;
    }
  }

  info(text?: string): void {
    if (this.verbose) {
      console.log(pc.cyan(`ℹ ${text || 'Info'}`));
      return;
    }

    if (this.spinner) {
      this.spinner.info(text);
      this.spinner = null;
    }
  }

  stop(): void {
    if (this.spinner) {
      this.spinner.stop();
      this.spinner = null;
    }
  }
}

export function createProgressManager(verbose = false): ProgressManager {
  return new ProgressManager(verbose);
}

// Utility for creating multi-step progress
export class MultiStepProgress {
  private current = 0;
  private total: number;
  private manager: ProgressManager;
  private steps: string[];

  constructor(steps: string[], verbose = false) {
    this.steps = steps;
    this.total = steps.length;
    this.manager = new ProgressManager(verbose);
  }

  start(): void {
    this.current = 0;
    this.updateProgress();
  }

  next(customText?: string): void {
    if (this.current < this.total - 1) {
      this.current++;
      this.updateProgress(customText);
    }
  }

  complete(text = 'All steps completed'): void {
    this.manager.succeed(text);
  }

  fail(text?: string): void {
    this.manager.fail(text);
  }

  warn(text?: string): void {
    this.manager.warn(text);
  }

  updateCurrentStep(text: string): void {
    const step = this.current + 1;
    const progressText = `[${step}/${this.total}] ${text}`;
    this.manager.update(progressText);
  }

  private updateProgress(customText?: string): void {
    const step = this.current + 1;
    const text = customText || this.steps[this.current];
    const progressText = `[${step}/${this.total}] ${text}`;
    
    if (this.current === 0) {
      this.manager.start({ text: progressText });
    } else {
      this.manager.update(progressText);
    }
  }
}